import { SocialServiceSelectOfferTableStore } from './social-service-select-offer-table.store';
import { Component, computed, effect, inject, input, output, signal, untracked, viewChildren } from '@angular/core';
import { TableComponent } from '../../../../../modules/shared/components/kit/table/table.component';
import { TableColumn } from '../../../../../modules/shared/components/kit/table/table.types';
import { NumberInputComponent } from '../../../../kit/input/text-input/number-input.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { SocialServiceSelectOfferComponent } from '../social-service-select-offer.component';
import { WorkOfferUpsertInput } from '@libs/types/src/services/input-type/work/work-offer.upsert-input';
import { compareArrays } from '@libs/types/src/utils/array.utils';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import {
    SocialServiceOfferDetailDialogComponent,
} from '../social-service-offer-detail-dialog/social-service-offer-detail-dialog.component';
import { OfferSelectOption } from '../social-service-select-offer.types';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { MultiSelectInputComponent } from '../../../../kit/input/multi-select-input/multi-select-input.component';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { StripAndTrimPipe } from '../../../../../modules/shared/pipes/strip-and-trim.pipe';
import {
    SocialServiceOfferNoteDialogComponent,
} from '../social-service-offer-note-dialog/social-service-offer-note-dialog.component';
import { translate } from '../../../../../modules/shared/shared.global-service';
import { SocialServiceProcedureTypeEnum } from '@libs/types/src/services/enum/social-service-intervention-type.enum';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { getServiceAreasFrag } from '../../work-individual-work/work-individual-work.query';
import { serviceTypeNameAllowed } from '@libs/types/src/services/enum/service-type.enum';
import { TagsInputComponent } from '../../../../kit/input/tags-input/tags-input.component';
import { getEmploymentEnumsByParentGroup } from '@libs/types/src/user/enum/employment.enum';
import { EmploymentParentGroupEnum } from '@libs/types/src/user/enum/employment-parent-group.enum';
import {
    InputUserMultiselectComponent,
} from '../../../../../modules/shared/components/inputs/input-user-multiselect/input-user-multiselect.component';
import {
    StripAndShowComponent,
} from '../../../../../modules/shared/components/kit/strip-and-show/strip-and-show.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ChipComponent } from '../../../../kit/chip/chip.component';
import { FolderGridItemObject } from '@it2go/types';
import { UserChipComponent } from '../../../../kit/user-chip/user-chip.component';
import { UserChipRenderTypeEnum } from '../../../../kit/user-chip/user-chip.component.types';
import { disabled, form, FormField } from '@angular/forms/signals';
import { CheckInputComponent } from '../../../../kit/input/check-input-v2/check-input.component';
import { WorkOfferTableItem } from './social-service-select-offer-table.types';

@Component({
    selector: 'app-social-service-select-offer-table',
    templateUrl: './social-service-select-offer-table.component.html',
    styleUrl: './social-service-select-offer-table.component.scss',
    providers: [SocialServiceSelectOfferTableStore],
    imports: [
        TableComponent,
        NumberInputComponent,
        PrimeTemplate,
        SelectInputComponent,
        MultiSelectInputComponent,
        ButtonComponent,
        StripAndTrimPipe,
        TagsInputComponent,
        InputUserMultiselectComponent,
        StripAndShowComponent,
        TranslatePipe,
        ChipComponent,
        UserChipComponent,
        CheckInputComponent,
        FormField,
    ],
})
export class SocialServiceSelectOfferTableComponent {

    protected readonly store = inject(SocialServiceSelectOfferTableStore);
    protected readonly dialogService = inject(DialogService);

    public showFolders = input(false);
    public tableKey = input.required<string>();
    public maxApplication = input<number | undefined, number | null | undefined>(undefined, { transform: v => v ?? undefined });
    public setMaxApplicationOnInit = input<boolean>(false);
    public defaultPresence = input<string | null | undefined>();
    public showDurationOther = input(false);
    public showDetail = input(false);
    public disabled = input(false);
    public serviceId = input.required<number, any>({ transform: v => Number(v || 0) });
    public folder = input<FolderGridItemObject | null>(null);
    public dateFrom = input<Date, any>(new Date(), { transform: v => v ?? new Date() });
    public offers = input<(WorkOfferUpsertInput | any)[], WorkOfferUpsertInput[] | null | undefined>([], { transform: v => v || [] });

    public updatedValues = output<object>({});

    public addUser = output<string>();
    public removeUser = output<string>();

    public items = signal<WorkOfferUpsertInput[]>([]);

    public offerSelect = input<SocialServiceSelectOfferComponent | null | undefined>(null);
    public offerSelectOptions = input<OfferSelectOption[], any>([], { transform: v => v || [] });

    protected readonly applicationsInputs = viewChildren<NumberInputComponent>('applicationsInput');
    protected readonly durationOtherInputs = viewChildren<NumberInputComponent>('durationOtherInput');
    protected readonly typeSelects = viewChildren<SelectInputComponent>('selectType');
    protected readonly presenceSelects = viewChildren<SelectInputComponent>('selectPresence');
    protected readonly areaSelects = viewChildren<MultiSelectInputComponent>('selectAreas');
    protected readonly tagInputs = viewChildren<TagsInputComponent>('selectTags');
    protected readonly usersInputs = viewChildren<MultiSelectInputComponent>('selectUserComponent');

    protected prefix = 'workData.offer.';

    // for folders in credit group
    public creditOffers = input<Record<number, number[]>>({});
    protected readonly creditSelections = signal<Record<string, boolean>>({});
    protected readonly creditFields = form(this.creditSelections, schema => {
        disabled(schema, () => this.disabled());
    });

    protected columns = computed<TableColumn[]>(() => {
        return [
            ...(this.showFolders() ? [{ name: 'folder' }] : []),
            { name: 'code' },
            { name: 'name' },
            { name: 'unit' },
            { name: 'applications' },
            ...(this.showDurationOther() ? [{ name: 'durationOther' }] : []),
            { name: 'type' },
            { name: 'presence' },
            ...(this.isAreaAllowed() ? [{ name: 'areas' }] : []),
            { name: 'users' },
            { name: 'tags' },
            { name: 'note' },
            ...(Object.keys(this.creditSelections()).length ? [{ name: 'payByCredits' }] : []),
        ];
    });

    protected employments = [EmploymentParentGroupEnum.DIRECT_CARE, EmploymentParentGroupEnum.SUPPORTIVE_CARE];

    protected isAreaAllowed = computed(() => {
        const service = this.store.globalGlobalStore.organizationStore.getServiceBySignalId(this.serviceId)();

        return serviceTypeNameAllowed.socialServiceArea.includes(service?.serviceType?.name || '');
    });

    constructor() {
        effect(() => {
            this.store.serviceId.set(this.serviceId());
            const filter = new FilterBuilder({ serviceId: this.serviceId() || 0 });
            filter.validAt(this.dateFrom());
            this.store.api.getServiceSocialAreas(filter.singlePage(), getServiceAreasFrag);
        });

        effect(() => {
            this.store.fetchOffersData(this.offers()?.map((it) => it.offerId) || []);
        });

        effect(() => {
            // Keep existing rows when their values change so inputs do not lose focus.
            this.offers();
            this.refreshTable();
        });

        effect(() => {
            this.setTableValues();
        });

        // for folders in credit group
        effect(() => this.updateCreditSelections());
    }

    public refreshTable(checkSame: boolean = true): void {
        const current = untracked(() => this.items());
        const newItems = this.offers();

        if (checkSame) {
            const diff = compareArrays(
                current.map((it: any) => `${it.offerId}_${it.folder?.id || 0}`),
                newItems.map((it: any) => `${it.offerId}_${it.folder?.id || 0}`),
            );
            if (diff.isSame) {
                current.forEach((item: WorkOfferTableItem) => {
                    const offer = newItems.find((offer: WorkOfferTableItem) =>
                        offer.offerId === item.offerId && offer.folder?.id === item.folder?.id,
                    );
                    Object.assign(item, offer);
                });
                this.items.set([...current]);
                this.setTableValues();
                return;
            }
        }
        const maxValue = this.maxApplication();
        if (this.setMaxApplicationOnInit() && maxValue) {
            newItems.forEach((it) => {
                if (it.applications <= 1) {
                    it.applications = maxValue;
                }
            });
        }

        this.items.set([...newItems]);
        this.setTableValues();
    }

    protected detail(_item: WorkOfferUpsertInput | any): void {
        // Items can have older values (as table is not refresh to not lose focus
        const item = this.getOffersItem(_item);
        const option = this.getOption(item.offerId, item?.folder?.id)!;

        this.dialogService.open(
            SocialServiceOfferDetailDialogComponent,
            {
                header: `${option.code} - ${option.name}`,
                styleClass: 'p-dialog-width-80',
                data: {
                    ...item,
                    serviceId: this.serviceId(),
                    folderId: _item.folder?.id || this.folder()?.id || 0,
                    employments: this.employments,
                    showDurationOther: this.showDurationOther(),
                },
            },
        )?.onClose.subscribe((newData: WorkOfferUpsertInput | null) => this.updateFromDialog(newData, item));
    }

    protected noteDetail(_item: WorkOfferUpsertInput | any): void {
        // Items can have older values (as table is not refresh to not lose focus
        const item = this.getOffersItem(_item);
        const intervention = item.type !== SocialServiceProcedureTypeEnum.PROCEDURE;
        const option = this.getOption(item.offerId, item?.folder?.id)!;

        this.dialogService.open(
            SocialServiceOfferNoteDialogComponent,
            {
                header: `${translate.instant(`workData.individualWork.message.${intervention ? 'interventionNote' : 'procedureNote'}`)}: ${option.name}`,
                styleClass: 'p-dialog-width-80',
                data: {
                    ...item,
                    serviceId: this.serviceId(),
                },
            },
        )?.onClose.subscribe((newData: WorkOfferUpsertInput | null) => this.updateFromDialog(newData, item));
    }

    protected queryTags(search: string): void {
        this.store.api.searchWorkOfferTags({
            search,
            limit: 100,
            serviceId: this.serviceId(),
        });
    }

    protected setValue(value: any, key: string, offerId: number, item?: any): void {
        const select = this.offerSelect();
        const offer = this.offers().find((it: WorkOfferUpsertInput | any) =>
            it.offerId == offerId && (!item || item?.folder?.id == it?.folder?.id),
        );
        const option = this.getOption(offerId, item?.folder?.id);
        if (!offer) return;

        switch (key) {
            case 'applications':
                option && select?.itemOnInput(option, { value } as any);
                if (!select) {
                    offer.applications = value;
                    if (item) item.applications = value;
                }
                break;
            case 'durationOther':
                const currentDuration =
                    (offer.durationNote || 0)
                    + (offer.durationPreparation || 0)
                    + (offer.durationSelfReflection || 0)
                ;
                offer.durationNote = (offer.durationNote || 0) + value - currentDuration;
                if (offer.durationNote < 0) {
                    offer.durationPreparation = (offer.durationPreparation || 0) + offer.durationNote;
                    offer.durationNote = 0;
                    if (offer.durationPreparation < 0) {
                        offer.durationSelfReflection = (offer.durationSelfReflection || 0) + offer.durationPreparation;
                        offer.durationPreparation = 0;
                    }
                }

                break;
            case 'users':
                this.updateUsers(offer, value);
                if (item) item.users = offer.users;
                break;
            default:
                (offer as any)[key] = value;
                if (item) (item as any)[key] = value;
                break;
        }

        this.updatedValues.emit({});
    }

    private updateFromDialog(newData: any, item: WorkOfferUpsertInput | any): void {
        if (!newData) return;
        const option = this.getOption(item.offerId, item?.folder?.id);
        if (!option) return;

        // A = removed, B = added
        this.updateUsers(item, newData['users']);
        Object.entries(newData).forEach(([key, value]) => {
            (item as any)[key] = value;
        });

        this.offerSelect()?.itemOnInput(option, { value: newData.applications } as any);
        this.refreshTable(false);
    }

    private getOffersItem(_item: WorkOfferUpsertInput | any) {
        return this.offers().find((it: any) =>
            it.offerId == _item.offerId && (!_item?.folder?.id || _item?.folder?.id == it?.folder?.id),
        );
    }

    private getOption(offerId: number, folderId?: number): OfferSelectOption | null {
        return (this.offerSelect()?.offerOptions() || this.offerSelectOptions())?.find((it: any) =>
            it.offerId == offerId && (!folderId || !it.folder?.id || folderId == it.folder?.id)
        ) || null;
    }

    private setTableValues(): void {
        const durationOtherInputs = this.durationOtherInputs();
        const userInputs = this.usersInputs();
        const basicInputs: Record<string, any> = {
            applications: this.applicationsInputs(),
            type: this.typeSelects(),
            presence: this.presenceSelects(),
            areas: this.areaSelects(),
            tags: this.tagInputs(),
        };

        this.offers().forEach((it: WorkOfferUpsertInput | any) => {
            if (typeof it !== 'object') return;
            if (!it.type) {
                it.type = this.store.typeOptions()?.[0]?.value || '';
                it.presence = this.defaultPresence() || this.store.presenceOptions()?.[0]?.value || '';
            }

            const durationOtherInput = durationOtherInputs.filter((ij) =>
                ij.attributes().offerId == it.offerId && ij.attributes().folderId == (it.folder?.id || this.folder()?.id || 0),
            );
            durationOtherInput.forEach(input =>
                input?.setValue((it.durationNote || 0) + (it.durationPreparation || 0) + (it.durationSelfReflection || 0)),
            );

            userInputs.filter((ij) =>
                ij.attributes().offerId == it.offerId && ij.attributes().folderId == (it.folder?.id || this.folder()?.id || 0),
            )
                .forEach((input) => input?.setValue(it.users || [], true))
            ;

            Object.entries(basicInputs).forEach(([key, inputs]) => {
                inputs.filter((ij: any) =>
                    ij.attributes().offerId == it.offerId && ij.attributes().folderId == (it.folder?.id || this.folder()?.id || 0),
                ).forEach((input: any) => input?.setValue(it[key]))
                ;
            });
        });
    }

    private updateUsers(item: WorkOfferUpsertInput | any, newUsers: string[]): void {
        const userDiff = compareArrays(item.users || [], newUsers);

        userDiff.aUnique.forEach((userId) => {
            const inUsage = this.items().some((it: WorkOfferUpsertInput | any) =>
                    (
                        it.offerId !== item.offerId
                        || (it.folder?.id || 0) !== (item?.folder?.id || this.folder()?.id || 0)
                    )
                    && it.users?.includes(userId)
                ,
            );
            if (!inUsage) {
                this.removeUser.emit(userId);
            }
        });
        userDiff.bUnique.forEach((userId) => {
            const inUsage = this.items().some((it: WorkOfferUpsertInput | any) =>
                    (
                        it.offerId !== item.offerId
                        || (it.folder?.id || 0) !== (item?.folder?.id || this.folder()?.id || 0)
                    )
                    && it.users?.includes(userId)
                ,
            );
            if (!inUsage) {
                this.addUser.emit(userId);
            }
        });
        item.users = newUsers;
    }

    private updateCreditSelections(): void {
        const selections: Record<string, boolean> = {};
        const inCreditGroup = this.creditOffers();
        for (const offer of this.offers() as WorkOfferTableItem[]) {
            const folderId = offer.folder?.id ?? this.folder()?.id;
            if (offer.payByCredits === true || (folderId && inCreditGroup[folderId]?.includes(offer.offerId))) {
                offer.payByCredits ??= true;
                selections[this.creditSelectionKey(offer)] = offer.payByCredits;
            }
        }
        this.creditSelections.set(selections);
    }

    protected creditSelectionKey(offer: WorkOfferTableItem): string {
        return `${offer.folder?.id ?? this.folder()?.id}_${offer.offerId}`;
    }

    protected readonly getEmploymentEnumsByParentGroup = getEmploymentEnumsByParentGroup;
    protected readonly EmploymentParentGroupEnum = EmploymentParentGroupEnum;
    protected readonly UserChipRenderTypeEnum = UserChipRenderTypeEnum;
}
